import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Flex,
  Modal,
  Progress,
  Result,
  Row,
  Tag,
  Typography,
  message,
} from 'antd';
import { debounce, type DebouncedFunc } from 'lodash';
import { Check, Sparkles, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  getAutocompleteLeadsQueryKey,
  getCountRecordsUrl,
  getGetImportJobQueryKey,
  getListAiAssistantThreadsQueryKey,
  getListCustomFieldsQueryKey,
  getListDealsUrl,
  getListImportJobsQueryKey,
  getSearchRecordsUrl,
  useCreateAiAssistantThread,
  useDismissImportJob,
  useExecuteImportJob,
  useGetImportJob,
  useListCustomFields,
  useListDealStages,
  useListOrganizationMemberships,
  useRollbackImportJob,
  useUpdateImportMapping,
  useValidateImportJob,
} from '../../../../api';
import { useAiAssistant } from '../../../../modules/ai-next/providers';
import { trackCommandUsed } from '../../../../modules/google-analytics';
import {
  ImportAnalyzing,
  ImportLayout,
  ImportMapper,
  ImportReviewTable,
  ImportRunAgents,
  ImportStatTile,
  ImportTrickleProgress,
  useImportJobProgress,
} from '../../../../modules/import';
import { buildTargetOptions } from '../../../../modules/record-mapper/utils';
import { KIND_ICON, kindLabel } from '../../../../ui/components/field-mapper';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { TableSurface } from '../../../../ui/components/table-surface';
import { useNavigationPosition } from '../../../../ui/hooks';
import { useLayoutInsets } from '../../../../ui/providers/layout-insets';

import type {
  ImportJobResponseDto,
  StartAiAssistantThreadRequestDtoUiContextImportJobIssueGroupsItem,
} from '../../../../api';
import type { AiUiContext } from '../../../../modules/ai-next/types';
import type { FieldMapperValue } from '../../../../ui/components/field-mapper';

const ImportDetailRoute = () => {
  const navigate = useNavigate();
  const insets = useLayoutInsets();
  const { isSideNav } = useNavigationPosition();
  const { selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const { importJobId } = Route.useParams();

  const { data, isPending, refetch } = useGetImportJob(selectedOrganization.id, importJobId);
  const liveJob = useImportJobProgress(selectedOrganization.id)[importJobId];

  const queryClient = useQueryClient();
  const customFields = useListCustomFields(selectedOrganization.id);
  const dealStages = useListDealStages(selectedOrganization.id);
  const memberships = useListOrganizationMemberships(selectedOrganization.id);
  const { mutateAsync: saveMapping } = useUpdateImportMapping();
  const { mutateAsync: executeImport } = useExecuteImportJob();
  const { mutateAsync: validateImport } = useValidateImportJob();
  const { mutateAsync: rollbackImport, isPending: rollingBack } = useRollbackImportJob();
  const { mutateAsync: dismissImport, isPending: dismissing } = useDismissImportJob();

  const { manager, panel, uiContext } = useAiAssistant();
  const pendingAiUiContextRef = useRef<AiUiContext | undefined>(undefined);
  const { mutate: createAiThread } = useCreateAiAssistantThread({
    mutation: {
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: getListAiAssistantThreadsQueryKey(selectedOrganization.id),
        });
        manager.createAndStartThread(response.data, pendingAiUiContextRef.current);
        pendingAiUiContextRef.current = undefined;
        panel.openThread(response.data.id);
      },
    },
  });

  const [draft, setDraft] = useState<FieldMapperValue | null>(null);
  const [saving, setSaving] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, Record<string, string>>>({});
  const [validation, setValidation] = useState<ImportJobResponseDto['validation']>(null);
  const [reviewRows, setReviewRows] = useState<
    NonNullable<ImportJobResponseDto['validation']>['problemRows']
  >([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const { step } = Route.useSearch();
  const reviewStep = step === 'review' || step === 'resolve';
  const resolving = step === 'resolve';
  const agentsStep = step === 'agents';
  const goToStep = (next: 'review' | 'resolve' | 'agents' | undefined) =>
    navigate({
      to: '/import/$importJobId',
      params: { importJobId },
      search: next ? { step: next } : {},
    });

  const job = data?.data;

  const revalidateRef = useRef<(next: Record<string, Record<string, string>>) => Promise<void>>(
    async () => {},
  );

  revalidateRef.current = async (next) => {
    if (!job) return;
    try {
      const result = await validateImport({
        organizationId: selectedOrganization.id,
        importJobId: job.id,
        data: { overrides: next },
      });
      setValidation(result.data.validation);
    } catch {
      message.error('Could not re-check the file');
    }
  };

  const debouncedRevalidateRef = useRef<DebouncedFunc<
    (next: Record<string, Record<string, string>>) => void
  > | null>(null);
  if (!debouncedRevalidateRef.current) {
    debouncedRevalidateRef.current = debounce(
      (next: Record<string, Record<string, string>>) => void revalidateRef.current(next),
      600,
    );
  }

  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current || !job || job.status !== 'mapped') {
      return;
    }

    if (step !== 'review' && step !== 'resolve') {
      return;
    }

    initRef.current = true;
    if (validation !== null) {
      return;
    }

    setOverrides(job.cellOverrides ?? {});
    revalidateRef.current(job.cellOverrides ?? {});
  }, [job, step, validation]);

  useEffect(() => {
    if (!reviewStep) {
      setReviewRows([]);
      return;
    }

    if (!validation) return;
    setReviewRows((previous) => {
      const fresh = new Map(validation.problemRows.map((problem) => [problem.row, problem]));
      const kept = previous.map((problem) => fresh.get(problem.row) ?? problem);
      const seen = new Set(kept.map((problem) => problem.row));
      const additions = validation.problemRows.filter((problem) => !seen.has(problem.row));
      return [...kept, ...additions].sort((a, b) => a.row - b.row);
    });
  }, [reviewStep, validation]);

  const targetOptions = useMemo(
    () => buildTargetOptions(customFields.data?.data?.items ?? [], { includeFile: false }),
    [customFields.data?.data?.items],
  );
  const dealStageOptions = useMemo(
    () =>
      (dealStages.data?.data?.items ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((stage) => ({ value: stage.id, label: stage.name })),
    [dealStages.data?.data?.items],
  );
  const ownerOptions = useMemo(
    () =>
      (memberships.data?.data?.items ?? []).map((membership) => ({
        value: membership.id,
        label: `${membership.user.firstName} ${membership.user.lastName}`.trim() || 'Unknown',
        role: membership.role.name,
      })),
    [memberships.data?.data?.items],
  );

  const handleEditCell = (row: number, fieldId: string, value: string) => {
    setOverrides((previous) => {
      const next = {
        ...previous,
        [String(row)]: { ...(previous[String(row)] ?? {}), [fieldId]: value },
      };
      debouncedRevalidateRef.current?.(next);
      return next;
    });
  };

  const startAssistant = (prompt: string) => {
    trackCommandUsed({ command: 'import_fix', source: 'shortcut', thread: 'new' });
    const { full, compact } = manager.snapshotUiContext();
    pendingAiUiContextRef.current = full;
    panel.open();
    panel.beginNewChat(prompt);
    createAiThread({
      organizationId: selectedOrganization.id,
      data: { message: prompt, uiContext: compact },
    });
  };

  useEffect(() => {
    if (!resolving) setSelectedRows(new Set());
  }, [resolving]);

  useEffect(() => {
    if (reviewStep && job?.status === 'mapped') {
      const selectedGroups = new Map<
        string,
        StartAiAssistantThreadRequestDtoUiContextImportJobIssueGroupsItem
      >();

      for (const problem of validation?.problemRows ?? []) {
        if (!selectedRows.has(String(problem.row))) {
          continue;
        }

        for (const issue of problem.issues) {
          const key = `${issue.fieldId}\u0000${issue.value}`;
          const group = selectedGroups.get(key) ?? {
            fieldId: issue.fieldId,
            field: issue.field,
            reason: issue.reason,
            value: issue.value ?? '',
            count: 0,
            rows: [],
            // Server groups are capped, so a selected stage issue past the cap still gets its options.
            options:
              validation?.issueGroups.find(
                (candidate) =>
                  candidate.fieldId === issue.fieldId && candidate.value === issue.value,
              )?.options ??
              (issue.reason === 'Unknown deal stage'
                ? dealStageOptions.map((option) => option.label)
                : undefined),
          };

          group.count += 1;
          if (group.rows.length < 5) {
            group.rows.push(problem.row);
          }

          selectedGroups.set(key, group);
        }
      }
      uiContext.setImportJob({
        importJobId,
        issueCount: validation?.rowsWithIssues ?? reviewRows.length,
        cellIssues: selectedRows.size
          ? [...selectedGroups.values()].reduce((sum, group) => sum + group.count, 0)
          : validation?.cellIssues,
        selectedRows: selectedRows.size ? [...selectedRows].map(Number) : undefined,
        issueGroups: selectedRows.size
          ? [...selectedGroups.values()].sort((left, right) => right.count - left.count)
          : validation?.issueGroups,
        issueGroupsTruncated: selectedRows.size ? false : validation?.issueGroupsTruncated,
      });
    } else {
      uiContext.clearImportJob();
    }
    return () => uiContext.clearImportJob();
  }, [
    reviewStep,
    job?.status,
    importJobId,
    validation,
    reviewRows.length,
    selectedRows,
    dealStageOptions,
    uiContext,
  ]);

  const syncedOverridesRef = useRef<string | null>(null);
  useEffect(() => {
    if (!reviewStep || job?.status !== 'mapped') {
      return;
    }

    const serverOverrides = (job.cellOverrides ?? {}) as Record<string, Record<string, string>>;
    const serialized = JSON.stringify([serverOverrides, job.valueOverrides]);
    if (syncedOverridesRef.current === null) {
      syncedOverridesRef.current = serialized;
      return;
    }

    if (serialized === syncedOverridesRef.current) {
      return;
    }

    syncedOverridesRef.current = serialized;
    setOverrides(serverOverrides);
    void revalidateRef.current(serverOverrides);
  }, [job?.cellOverrides, job?.valueOverrides, job?.status, reviewStep]);

  if (isPending) {
    return (
      <Flex align="center" justify="center" style={{ height: `calc(100dvh - ${insets.top}px)` }}>
        <Loader size="large" />
      </Flex>
    );
  }

  if (!job) {
    return (
      <Flex align="center" justify="center" style={{ height: `calc(100dvh - ${insets.top}px)` }}>
        <Result
          status="error"
          title="Something went wrong"
          subTitle="Please try again in a moment."
          extra={
            <Button type="primary" onClick={() => navigate({ to: '/import' })}>
              Back to imports
            </Button>
          }
        />
      </Flex>
    );
  }

  if (job.status === 'created' || job.status === 'uploaded' || job.status === 'analyzing') {
    return (
      <ImportLayout subtitle={job.fileName} currentStep={1}>
        <ImportAnalyzing
          fileName={job.fileName}
          onViewHistory={() => navigate({ to: '/import' })}
        />
      </ImportLayout>
    );
  }

  const stats = job.output.stats ?? {};
  const skipReasons = job.output.skipReasons ?? [];
  const createdCustomFields = job.output.createdCustomFields ?? [];
  const sourceLabelByCustomFieldId = new Map(
    (job.mapping?.fields ?? [])
      .flatMap((field) => [field, ...(field.itemMapping ?? [])])
      .flatMap((item) =>
        item.target?.customFieldId ? [[item.target.customFieldId, item.sourceLabel] as const] : [],
      ),
  );
  const total = liveJob?.rowsTotal ?? stats.rowsTotal ?? 0;
  const processed = liveJob?.rowsProcessed ?? stats.rowsProcessed ?? 0;

  const companiesCreated = stats.companiesCreated ?? 0;
  const companiesUpdated = stats.companiesUpdated ?? 0;
  const companiesTotal =
    companiesCreated + skipReasons.filter((reason) => reason.entity === 'company').length;
  const peopleCreated = stats.peopleCreated ?? 0;
  const peopleUpdated = stats.peopleUpdated ?? 0;
  const dealsCreated = stats.dealsCreated ?? 0;
  const dealsUpdated = stats.dealsUpdated ?? 0;
  const recordsCreated = companiesCreated + peopleCreated + dealsCreated;
  const rolledBackAt = job.output.rolledBackAt ?? null;
  const dismissedAt = job.output.dismissedAt ?? null;
  const canRollback =
    (job.status === 'completed' || job.status === 'failed') &&
    !rolledBackAt &&
    !dismissedAt &&
    recordsCreated > 0;
  const canRunAgents = job.status === 'completed' && !dismissedAt && !rolledBackAt;

  const handleContinue = async () => {
    const serverMapping = job.mapping;
    if (!serverMapping) return;
    setSaving(true);
    try {
      if (draft != null) {
        await saveMapping({
          organizationId: selectedOrganization.id,
          importJobId: job.id,
          data: {
            source: serverMapping.source,
            targets: draft.targets,
            dealStageId: draft.dealStageId,
            ownerMembershipId: draft.ownerMembershipId ?? selectedMembership.id,
            allowedFileExtensions: draft.allowedFileExtensions,
            fields: draft.fields,
            phase: 'mapped',
          },
        });
      }
      const startOverrides = (job.cellOverrides ?? {}) as Record<string, Record<string, string>>;
      const result = await validateImport({
        organizationId: selectedOrganization.id,
        importJobId: job.id,
        data: { overrides: startOverrides },
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomFieldsQueryKey(selectedOrganization.id),
      });
      setDraft(null);
      setOverrides(startOverrides);
      setValidation(result.data.validation);
      goToStep('review');
    } catch {
      message.error('Could not analyze the file');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async () => {
    setSaving(true);
    try {
      await validateImport({
        organizationId: selectedOrganization.id,
        importJobId: job.id,
        data: { overrides },
      });
      await executeImport({ organizationId: selectedOrganization.id, importJobId: job.id });
      queryClient.invalidateQueries({
        queryKey: getListImportJobsQueryKey(selectedOrganization.id),
      });
      queryClient.invalidateQueries({
        queryKey: getGetImportJobQueryKey(selectedOrganization.id, job.id),
      });
      refetch();
    } catch {
      message.error('Could not start the import');
      setSaving(false);
      // The job may have moved on server-side (started elsewhere, or stopped) — show where it is
      // instead of leaving a review step whose Import can only fail again.
      refetch();
    }
  };

  const handleRollback = () => {
    Modal.confirm({
      title: 'Rollback this import?',
      content:
        'Archives everything created by this import. Records it matched and updated keep their new values.',
      okText: 'Rollback',
      okButtonProps: { danger: true, 'data-testid': 'confirm' },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await rollbackImport({
            organizationId: selectedOrganization.id,
            importJobId: job.id,
          });
          message.success('Import rolled back');
          for (const queryKey of [
            getGetImportJobQueryKey(selectedOrganization.id, job.id),
            getListImportJobsQueryKey(selectedOrganization.id),
            getAutocompleteLeadsQueryKey(selectedOrganization.id),
            [getSearchRecordsUrl(selectedOrganization.id)],
            [getCountRecordsUrl(selectedOrganization.id)],
            [getListDealsUrl(selectedOrganization.id)],
          ]) {
            queryClient.invalidateQueries({ queryKey });
          }
          refetch();
        } catch {
          message.error('Could not roll back the import');
        }
      },
    });
  };

  const handleDismiss = () => {
    const running = job.status === 'processing';
    const finished = job.status === 'completed' || job.status === 'failed';
    Modal.confirm({
      title: 'Stop this import?',
      content: running
        ? 'Processing stops and the import is closed out. Records created so far stay in your CRM.'
        : finished
          ? 'It becomes read-only — you won’t be able to roll it back or run agents on it afterward. Records it created stay in your CRM.'
          : 'This import won’t run and is removed from your active imports. You can start a new import from your file anytime.',
      okText: 'Stop',
      okButtonProps: { danger: true, 'data-testid': 'confirm' },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await dismissImport({
            organizationId: selectedOrganization.id,
            importJobId: job.id,
          });
          queryClient.setQueryData(
            getGetImportJobQueryKey(selectedOrganization.id, job.id),
            response,
          );
          message.success('Import stopped');
        } catch {
          message.error('Could not stop the import');
        }
      },
    });
  };

  const mappedFields = (job.mapping?.fields ?? [])
    .filter((field) => field.target)
    .map((field) => ({
      id: field.id,
      label: field.sourceLabel,
      stages:
        field.target?.recordType === 'deal' && field.target.field === 'stage'
          ? (dealStages.data?.data?.items ?? []).map((stage) => ({
              id: stage.id,
              name: stage.name,
              color: stage.color,
            }))
          : undefined,
    }));
  const problemRows = validation?.problemRows ?? [];
  const issueRows = problemRows.flatMap((problem) =>
    problem.issues.map((issue) => ({
      row: problem.row,
      value: issue.value,
      field: issue.field,
      reason: issue.reason,
    })),
  );

  if (job.status === 'mapped' && resolving) {
    return (
      <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)` }}>
        <Flex
          align="center"
          justify="space-between"
          gap={16}
          style={{
            padding: `12px 24px 12px ${isSideNav ? 0 : 24}px`,
            borderBottom: '1px solid var(--ant-color-border-secondary)',
          }}
        >
          <Flex align="center" gap={12} style={{ flex: 1 }}>
            <Typography.Text strong style={{ fontSize: 14 }}>
              Resolve import issues
            </Typography.Text>
            <Flex align="center" gap={4}>
              <Check size={14} color="var(--ant-color-success)" />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Mapping saved
              </Typography.Text>
            </Flex>
          </Flex>
          <Flex align="center" gap={10}>
            {(validation?.rowsWithIssues ?? reviewRows.length) > 0 && (
              <Button
                icon={<Sparkles size={14} />}
                disabled={saving}
                onClick={() =>
                  startAssistant(
                    selectedRows.size > 0
                      ? `Resolve the import issues on my ${selectedRows.size} selected row${
                          selectedRows.size === 1 ? '' : 's'
                        }.`
                      : 'Resolve all the flagged rows in this import.',
                  )
                }
              >
                {selectedRows.size > 0 ? `Fix ${selectedRows.size} with AI` : 'Fix with AI'}
              </Button>
            )}
            <Button type="primary" loading={saving} onClick={() => goToStep('review')}>
              Next
            </Button>
          </Flex>
        </Flex>
        <TableSurface
          className={`table-surface--page-inset${isSideNav ? '' : ' table-surface--page-inset-top-nav table-surface--full-round'}`}
          style={{ paddingTop: 16 }}
        >
          {validation ? (
            <ImportReviewTable
              organizationId={selectedOrganization.id}
              rows={reviewRows}
              validation={validation}
              fields={mappedFields}
              overrides={overrides}
              valueOverrides={job.valueOverrides ?? {}}
              onEdit={handleEditCell}
              onStagesChange={() => void revalidateRef.current(overrides)}
              selectedRows={selectedRows}
              onSelectedRowsChange={setSelectedRows}
              disabled={saving}
            />
          ) : (
            <Flex flex={1} align="center" justify="center">
              <Loader />
            </Flex>
          )}
        </TableSurface>
      </Flex>
    );
  }

  return (
    <ImportLayout
      subtitle={job.fileName}
      currentStep={
        job.status === 'completed' && agentsStep
          ? 4
          : job.status === 'completed' || job.status === 'processing' || job.status === 'failed'
            ? 3
            : reviewStep
              ? 2
              : 1
      }
      stepStatus={job.status === 'failed' ? 'error' : 'process'}
      footer={
        <Flex align="center" gap={10} style={{ width: '100%' }}>
          {!dismissedAt && (
            <Button type="text" danger loading={dismissing} onClick={handleDismiss}>
              Stop
            </Button>
          )}
          <Flex align="center" gap={10} style={{ marginLeft: 'auto' }}>
            {job.status === 'mapped' ? (
              reviewStep ? (
                <>
                  <Button disabled={saving} onClick={() => goToStep(undefined)}>
                    Back
                  </Button>
                  <Button
                    type="primary"
                    data-testid="import-start"
                    loading={saving}
                    onClick={handleImport}
                  >
                    Import
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  data-testid="import-continue"
                  loading={saving}
                  onClick={handleContinue}
                >
                  Continue
                </Button>
              )
            ) : job.status === 'completed' ? (
              agentsStep ? (
                <>
                  <Button onClick={() => goToStep(undefined)}>Back</Button>
                  <Button type="primary" onClick={() => navigate({ to: '/import' })}>
                    Done
                  </Button>
                </>
              ) : (
                <>
                  {canRollback && (
                    <Button
                      variant="text"
                      color="danger"
                      data-testid="import-rollback"
                      loading={rollingBack}
                      onClick={handleRollback}
                    >
                      Rollback
                    </Button>
                  )}
                  <Button
                    type="primary"
                    onClick={() =>
                      canRunAgents ? goToStep('agents') : navigate({ to: '/import' })
                    }
                  >
                    {canRunAgents ? 'Continue' : 'Done'}
                  </Button>
                </>
              )
            ) : job.status === 'failed' ? (
              <>
                {canRollback && (
                  <Button
                    type="text"
                    danger
                    data-testid="import-rollback"
                    loading={rollingBack}
                    onClick={handleRollback}
                  >
                    Rollback
                  </Button>
                )}
                <Button onClick={() => navigate({ to: '/import' })}>Back</Button>
              </>
            ) : (
              <Button onClick={() => navigate({ to: '/import' })}>Back</Button>
            )}
          </Flex>
        </Flex>
      }
    >
      {job.status === 'mapped' && reviewStep ? (
        <Flex vertical gap={16}>
          <Flex align="center" gap={4}>
            <Check size={14} color="var(--ant-color-success)" />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Mapping saved
            </Typography.Text>
          </Flex>
          {!validation || validation.problemRows.length === 0 ? (
            <Flex vertical align="center" gap={8} style={{ paddingBlock: 40 }}>
              <Typography.Text strong style={{ fontSize: 15 }}>
                Everything looks good
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                No missing or invalid values found
                {validation
                  ? ` — all ${validation.rowsTotal.toLocaleString()} rows will import`
                  : ''}
                .
              </Typography.Text>
            </Flex>
          ) : (
            <>
              <Alert
                type="warning"
                showIcon
                title={`${validation.rowsWithIssues.toLocaleString()} of ${validation.rowsTotal.toLocaleString()} rows have issues`}
                description={
                  validation.rowsDropped > 0
                    ? `${validation.rowsDropped.toLocaleString()} rows without a Company name will be skipped unless you fix them. Resolve to edit the values inline, or import now and skip them.`
                    : 'The invalid values will be left out unless you fix them. Resolve to edit the values inline, or import now.'
                }
                action={
                  <Button type="primary" onClick={() => goToStep('resolve')}>
                    Resolve
                  </Button>
                }
              />
              <List
                items={[
                  ...issueRows.slice(0, 8).map((issue, index) => ({ issue, index })),
                  ...(validation.cellIssues > 8 ? [{ issue: null, index: -1 }] : []),
                ]}
                renderItem={({ issue, index }) => ({
                  key: String(index),
                  reactNode: issue ? (
                    <Flex align="center" justify="space-between" gap={12}>
                      <Flex align="center" gap={11}>
                        <Typography.Text strong style={{ fontSize: 13 }}>
                          Row {issue.row}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                          {issue.value || `empty ${issue.field ?? 'value'}`}
                        </Typography.Text>
                      </Flex>
                      <Tag variant="outlined" color="error" style={{ fontSize: 11 }}>
                        {issue.reason}
                      </Tag>
                    </Flex>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      +{(validation.cellIssues - 8).toLocaleString()} more
                    </Typography.Text>
                  ),
                })}
              />
            </>
          )}
        </Flex>
      ) : job.status === 'mapped' ? (
        <ImportMapper
          mapping={job.mapping}
          value={draft}
          targetOptions={targetOptions}
          dealStages={dealStageOptions}
          owners={ownerOptions}
          defaultOwnerMembershipId={selectedMembership.id}
          onChange={setDraft}
        />
      ) : job.status === 'processing' ? (
        <Flex
          vertical
          align="center"
          justify="center"
          gap={12}
          flex={1}
          style={{ paddingBlock: 40 }}
        >
          <Typography.Text strong style={{ fontSize: 15 }}>
            Importing your records
          </Typography.Text>
          {processed > 0 && total > 0 ? (
            <>
              <Progress
                percent={Math.round((processed / total) * 100)}
                strokeColor="var(--ant-color-primary)"
                style={{ width: '80%' }}
              />
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {processed.toLocaleString()} / {total.toLocaleString()} rows
              </Typography.Text>
            </>
          ) : (
            <>
              <ImportTrickleProgress style={{ width: '80%' }} />
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {total ? `Processing ${total.toLocaleString()} rows…` : 'Preparing…'}
              </Typography.Text>
            </>
          )}
        </Flex>
      ) : job.status === 'completed' && agentsStep ? (
        <ImportRunAgents
          organizationId={selectedOrganization.id}
          importedCount={companiesCreated + companiesUpdated}
          onCreateAgent={() => navigate({ to: '/agents/new' })}
        />
      ) : (
        <Flex vertical gap={24} data-testid="import-summary">
          {dismissedAt && (
            <Alert
              type="info"
              showIcon
              title="Import stopped"
              description="This import is read-only. No agents will run on it and it can no longer be rolled back. Any records it created remain in your CRM."
            />
          )}

          {job.status === 'failed' && !dismissedAt && (
            <Alert
              type="error"
              showIcon
              title="Import failed"
              description={
                (stats.failedChunks ?? 0) > 0
                  ? `${stats.failedChunks} chunk(s) could not be processed. Records from the successful chunks were still imported.`
                  : 'Something went wrong while processing this file.'
              }
            />
          )}

          {rolledBackAt && (
            <Alert
              type="info"
              showIcon
              title="Import rolled back"
              description="Every company, person, and deal this import created has been archived. Restore them individually from their lists. Records it matched and updated were kept as they are, along with custom fields and options the import added."
            />
          )}

          <Row gutter={[12, 12]}>
            <Col xs={12} sm={8}>
              <ImportStatTile
                title="Companies created"
                value={companiesCreated}
                testId="stat-companies-created"
              />
            </Col>
            <Col xs={12} sm={8}>
              <ImportStatTile title="Companies updated" value={companiesUpdated} />
            </Col>
            <Col xs={24} sm={8}>
              <ImportStatTile
                title="Columns mapped"
                value={(job.mapping?.fields ?? []).filter((field) => field.target).length}
              />
            </Col>
          </Row>

          {skipReasons.length > 0 && (
            <Flex vertical gap={12}>
              <Flex align="center" gap={8}>
                <Typography.Text type="secondary" strong style={{ fontSize: 11 }}>
                  VALIDATION
                </Typography.Text>
                <Tag variant="outlined" style={{ fontSize: 11, padding: '0 8px' }}>
                  {stats.recordsSkipped ?? skipReasons.length} SKIPPED
                </Tag>
              </Flex>

              <Card size="small">
                <Flex align="center" gap={12}>
                  <Avatar
                    size={36}
                    shape="square"
                    icon={<TriangleAlert size={16} />}
                    style={{
                      background: 'var(--ant-color-error-bg)',
                      color: 'var(--ant-color-error)',
                    }}
                  />
                  <Flex vertical>
                    <Typography.Text strong style={{ fontSize: 14 }}>
                      {skipReasons.length.toLocaleString()} records couldn&apos;t be created
                    </Typography.Text>
                    {companiesTotal > 0 && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {companiesCreated} of {companiesTotal} companies imported.
                      </Typography.Text>
                    )}
                  </Flex>
                </Flex>
              </Card>

              <List
                items={[
                  ...skipReasons.slice(0, 8).map((reason, index) => ({ reason, index })),
                  ...(skipReasons.length > 8 ? [{ reason: null, index: -1 }] : []),
                ]}
                renderItem={({ reason, index }) => ({
                  key: String(index),
                  reactNode: reason ? (
                    <Flex align="center" justify="space-between" gap={12}>
                      <Flex align="center" gap={11}>
                        <Typography.Text strong style={{ fontSize: 13 }}>
                          Row {reason.row}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                          {reason.value || `empty ${reason.field ?? 'value'}`}
                        </Typography.Text>
                      </Flex>
                      <Tag variant="outlined" color="error" style={{ fontSize: 11 }}>
                        {reason.reason}
                      </Tag>
                    </Flex>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      +{(skipReasons.length - 8).toLocaleString()} more
                    </Typography.Text>
                  ),
                })}
              />

              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Records with a missing or invalid value can&apos;t be created in your CRM, so
                they&apos;re left out of this import. Fix them in your file and re-upload to add
                them.
              </Typography.Text>
            </Flex>
          )}

          {peopleCreated +
            peopleUpdated +
            dealsCreated +
            dealsUpdated +
            (stats.companiesLinkedToPeople ?? 0) +
            (stats.companiesWithoutPerson ?? 0) >
            0 && (
            <Flex vertical gap={12}>
              <Typography.Text type="secondary" strong style={{ fontSize: 11 }}>
                RECORD LINKING
              </Typography.Text>
              <Row gutter={[12, 12]}>
                <Col xs={12} sm={8}>
                  <ImportStatTile
                    title="Companies linked to people"
                    value={stats.companiesLinkedToPeople ?? 0}
                  />
                </Col>
                <Col xs={12} sm={8}>
                  <ImportStatTile
                    title="People created"
                    value={peopleCreated}
                    testId="stat-people-created"
                  />
                </Col>
                <Col xs={12} sm={8}>
                  <ImportStatTile title="People updated" value={peopleUpdated} />
                </Col>
                <Col xs={12} sm={8}>
                  <ImportStatTile title="Deals created" value={dealsCreated} />
                </Col>
                <Col xs={12} sm={8}>
                  <ImportStatTile title="Deals updated" value={dealsUpdated} />
                </Col>
                <Col xs={12} sm={8}>
                  <ImportStatTile
                    title="Companies without a person"
                    value={stats.companiesWithoutPerson ?? 0}
                  />
                </Col>
              </Row>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Rows sharing a company become one company. A company already in your CRM is matched
                by website, then by name; a person by email, then by phone — those records are
                updated instead of duplicated, and blank cells keep their current values.
              </Typography.Text>
            </Flex>
          )}

          {createdCustomFields.length > 0 && (
            <Flex vertical gap={12}>
              <Flex align="center" gap={8}>
                <Typography.Text type="secondary" strong style={{ fontSize: 11 }}>
                  CUSTOM FIELDS CREATED
                </Typography.Text>
                <Tag variant="outlined" style={{ fontSize: 11, padding: '0 8px' }}>
                  {createdCustomFields.length}
                </Tag>
              </Flex>
              <Flex vertical gap={8}>
                {createdCustomFields.map((field) => {
                  const sourceLabel = sourceLabelByCustomFieldId.get(field.id);

                  return (
                    <Card key={field.id} size="small">
                      <Flex align="center" justify="space-between" gap={12}>
                        <Flex align="center" gap={12}>
                          <Flex
                            align="center"
                            justify="center"
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 999,
                              fontSize: 15,
                              color: 'var(--ant-color-warning-text)',
                              backgroundColor: 'var(--ant-color-warning-bg)',
                            }}
                          >
                            {KIND_ICON[field.type]}
                          </Flex>
                          <Flex vertical>
                            <Typography.Text strong style={{ fontSize: 14 }}>
                              {field.name}
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                              {field.recordType === 'lead'
                                ? 'Companies'
                                : field.recordType === 'contact'
                                  ? 'People'
                                  : 'Deals'}
                              {sourceLabel ? ` · from “${sourceLabel}” column` : ''}
                            </Typography.Text>
                          </Flex>
                        </Flex>
                        <Tag
                          icon={KIND_ICON[field.type]}
                          variant="outlined"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 11,
                            color: 'var(--ant-color-warning-text)',
                            borderColor: 'var(--ant-color-warning-border)',
                            backgroundColor: 'var(--ant-color-warning-bg)',
                          }}
                        >
                          {kindLabel(field.type)}
                        </Tag>
                      </Flex>
                    </Card>
                  );
                })}
              </Flex>
            </Flex>
          )}
        </Flex>
      )}
    </ImportLayout>
  );
};

export const Route = createFileRoute('/_authenticated/_app/import/$importJobId')({
  component: ImportDetailRoute,
  validateSearch: (
    search: Record<string, unknown>,
  ): { step?: 'review' | 'resolve' | 'agents' } => ({
    step:
      search.step === 'review' || search.step === 'resolve' || search.step === 'agents'
        ? search.step
        : undefined,
  }),
});
